import { useEffect, useState, useRef, useCallback } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Video from 'yet-another-react-lightbox/plugins/video';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import {
  FaBuilding, FaSubway, FaLandmark, FaShieldAlt,
  FaLeaf,
  FaPlay, FaArrowRight, FaChevronDown,
  FaMapMarkerAlt,
  FaChevronLeft, FaChevronRight, FaExpand
} from 'react-icons/fa';
import {
  HiOutlineSparkles, HiOutlineLightBulb,
  HiOutlineGlobeAlt, HiOutlineShieldCheck
} from 'react-icons/hi';
import {
  BiBuildingHouse, BiWorld
} from 'react-icons/bi';

/* ═══════════════════════════════════════════════════════════════════
   HOOKS
═══════════════════════════════════════════════════════════════════ */
function useInView(opts = {}) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); io.disconnect(); } },
      { threshold: 0.1, ...opts }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []); // eslint-disable-line
  return [ref, vis];
}

function useScrollY() {
  const [y, setY] = useState(0);
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => { setY(window.scrollY); ticking = false; });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return y;
}



/* ═══════════════════════════════════════════════════════════════════
   SUB‑COMPONENTS
═══════════════════════════════════════════════════════════════════ */
function Reveal({ children, className = '', delay = 0, direction = 'up' }) {
  const [ref, vis] = useInView();
  const hidden = {
    up: 'opacity-0 translate-y-14',
    down: 'opacity-0 -translate-y-14',
    left: 'opacity-0 translate-x-14',
    right: 'opacity-0 -translate-x-14',
    scale: 'opacity-0 scale-90',
  }[direction] || 'opacity-0 translate-y-14';
  return (
    <div ref={ref}
      className={`transition-all duration-700 ease-out ${vis ? 'opacity-100 translate-y-0 translate-x-0 scale-100' : hidden} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >{children}</div>
  );
}

function SectionHeading({ children, light = false }) {
  const [ref, vis] = useInView();
  return (
    <div ref={ref} className="text-center mb-10 md:mb-14">
      <h2 className={`inline-block font-extrabold relative pb-5 leading-tight ${light ? 'text-white' : 'text-gray-900'}`}
        style={{ fontSize: 'clamp(1.65rem, 3.5vw, 2.75rem)' }}>
        {children}
        <span className={`absolute bottom-0 left-1/2 h-1 rounded-full bg-gradient-to-r from-amber-500 to-yellow-400 transition-all duration-700 ease-out ${vis ? 'w-20 md:w-28 -translate-x-1/2' : 'w-0 -translate-x-1/2'}`} />
      </h2>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <Reveal className="text-center mb-3">
      <span className="inline-flex items-center gap-2 text-amber-600 font-semibold tracking-[0.18em] uppercase text-[11px] md:text-xs">
        <HiOutlineSparkles className="w-3.5 h-3.5" />
        {children}
      </span>
    </Reveal>
  );
}

/* ─── Optimised Image with lazy loading ─── */
function OptImg({ src, alt, className = '', ...rest }) {
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      className={className}
      {...rest}
    />
  );
}

/* ─── Interactive Before/After Slider ─── */
function ComparisonSlider({ beforeSrc, afterSrc, title }) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const updatePos = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setPos((x / rect.width) * 100);
  }, []);

  const onDown = (e) => { dragging.current = true; updatePos(e.clientX); e.target.setPointerCapture?.(e.pointerId); };
  const onMove = (e) => { if (dragging.current) updatePos(e.clientX); };
  const onUp = () => { dragging.current = false; };

  return (
    <div className="rounded-2xl overflow-hidden shadow-xl shadow-black/8 hover:shadow-2xl transition-shadow duration-500 bg-gray-900">
      <div ref={containerRef} className="relative aspect-[16/10] sm:aspect-video select-none cursor-col-resize touch-none"
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}>
        <OptImg src={afterSrc} alt="After" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
          <OptImg src={beforeSrc} alt="Before" className="absolute inset-0 w-full h-full object-cover"
            style={{ minWidth: containerRef.current?.offsetWidth || '100%' }} draggable={false} />
        </div>
        <div className="absolute top-0 bottom-0 w-0.5 bg-white/90 shadow-lg z-10" style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white shadow-xl border-2 border-amber-400 flex items-center justify-center z-20">
            <FaChevronLeft className="w-2.5 h-2.5 text-amber-500 -mr-0.5" />
            <FaChevronRight className="w-2.5 h-2.5 text-amber-500 -ml-0.5" />
          </div>
        </div>
        <span className="absolute top-2.5 left-2.5 sm:top-3 sm:left-3 bg-red-500/85 backdrop-blur-md text-white text-[10px] sm:text-[11px] font-bold uppercase tracking-widest px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full shadow-lg z-10">Before</span>
        <span className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 bg-emerald-500/85 backdrop-blur-md text-white text-[10px] sm:text-[11px] font-bold uppercase tracking-widest px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full shadow-lg z-10">After</span>
      </div>
    </div>
  );
}


/* ─── Particles ─── */
function Particles() {
  const ps = useRef(Array.from({ length: 24 }, (_, i) => ({
    id: i, x: Math.random() * 100, y: Math.random() * 100,
    size: Math.random() * 3 + 1.5, dur: Math.random() * 20 + 15,
    del: Math.random() * 10, op: Math.random() * 0.3 + 0.08,
  }))).current;
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1]">
      {ps.map(p => (
        <div key={p.id} className="absolute rounded-full bg-amber-300"
          style={{
            left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size, opacity: p.op,
            animation: `float ${p.dur}s ease-in-out ${p.del}s infinite alternate`
          }} />
      ))}
    </div>
  );
}



/* ═══════════════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════════════ */
export default function Future() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollY = useScrollY();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  /* ── data ── */
  const videoSlides = [
    {
      type: 'video', width: 1920, height: 1080, poster: '/assets/future/videos/poster-2.jpg',
      sources: [{ src: '/assets/future/videos/video-2.mp4', type: 'video/mp4' }, { src: '/assets/future/videos/video-2.webm', type: 'video/webm' }]
    },
    {
      type: 'video', width: 1920, height: 1080, poster: '/assets/future/videos/poster-3.jpg',
      sources: [{ src: '/assets/future/videos/video-3.mp4', type: 'video/mp4' }, { src: '/assets/future/videos/video-3.webm', type: 'video/webm' }]
    },
    {
      type: 'video', width: 1920, height: 1080, poster: '/assets/future/videos/poster-4.jpg',
      sources: [{ src: '/assets/future/videos/video-4.mp4', type: 'video/mp4' }, { src: '/assets/future/videos/video-4.webm', type: 'video/webm' }]
    },
  ];

  const comparisons = [
    { before: '/assets/future/before-parliament.jpg', after: '/assets/future/1.jpeg', title: 'Parliament Complex' },
    { before: '/assets/future/before-rajpath.jpg', after: '/assets/future/2.jpeg', title: 'Kartavya Path' },
    { before: '/assets/future/before-secretariat.jpg', after: '/assets/future/3.jpeg', title: 'Central Secretariat' },
    { before: '/assets/future/before-north-south-block.jpg', after: '/assets/future/4.jpeg', title: 'North & South Block' },
    { before: '/assets/future/before-vp-residence.jpg', after: '/assets/future/5.jpeg', title: 'Vice President Residence' },
    { before: '/assets/future/before-pm-office.jpg', after: '/assets/future/6.jpeg', title: 'Prime Minister Office' },
  ];

  const features = [
    { Icon: FaBuilding, title: 'Modern Complexes', desc: 'Interconnected structures with modern amenities, natural lighting, and efficient layouts designed for contemporary governance and working.' },
    { Icon: FaSubway, title: 'Underground Connectivity', desc: 'Advanced underground transit systems linking all major buildings for seamless high-efficiency movement throughout the zone.' },
    { Icon: FaLandmark, title: 'Cultural Spaces', desc: 'Heritage structures repurposed as public cultural venues—museums, galleries, and event halls opening history to the public.' },
    { Icon: FaShieldAlt, title: 'Retained Heritage', desc: 'Key landmarks, war memorials, and national archives preserved in their original form for future generations.' },
  ];




  const keyPillars = [
    { Icon: HiOutlineLightBulb, title: 'Innovation-Led Design', desc: 'Every structure integrates cutting-edge architecture with seismic resilience, energy-efficient engineering, and smart building automation systems.' },
    { Icon: HiOutlineGlobeAlt, title: 'World-Class Standards', desc: 'Designed to meet GRIHA 5-star and LEED Platinum certifications, setting a global benchmark for sustainable urban development.' },
    { Icon: HiOutlineShieldCheck, title: 'Heritage Sensitivity', desc: 'Meticulous preservation protocols ensure every protected monument, memorial, and heritage tree is maintained in its original state.' },
    { Icon: BiWorld, title: 'Public Accessibility', desc: 'Open green corridors, public plazas, museums, and cultural centres ensure the rejuvenated zone belongs to every citizen of India.' },
  ];

  const handleVideoClick = (i) => { setCurrentIndex(i); setIsOpen(true); };

  return (
    <div className="w-full overflow-x-hidden bg-gray-50" style={{ hyphens: 'none' }}>

      {/* ══════════════════════════════════════════════════════════════════
          1. HERO — Parallax + Particles
      ══════════════════════════════════════════════════════════════════ */}
      <section className="relative w-screen min-h-[520px] sm:min-h-[600px] h-[85vh] overflow-hidden -mx-[calc(50vw-50%)]">
        <OptImg src="/assets/future/middle.jpg" alt="Lutyens Delhi Transformation"
          className="absolute inset-0 w-full h-[120%] object-cover object-center will-change-transform"
          style={{ transform: `translateY(${scrollY * 0.22}px)` }} loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/45 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-tr from-amber-900/15 via-transparent to-indigo-900/10" />
        <Particles />

        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4 sm:px-6">
          <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 text-amber-300 text-[10px] sm:text-xs md:text-sm font-semibold uppercase tracking-[0.15em] sm:tracking-[0.2em] px-4 sm:px-5 py-1.5 sm:py-2 rounded-full mb-5 sm:mb-6 animate-[fadeUp_0.6s_ease-out]">
            <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-amber-400 animate-pulse" />
            India's Most Ambitious Urban Project
          </span>

          <h1 className="text-white font-extrabold leading-[1.08] drop-shadow-2xl animate-[fadeUp_0.85s_ease-out]"
            style={{ fontSize: 'clamp(2rem, 7vw, 5.5rem)', letterSpacing: '-0.02em' }}>
            The Lutyen's <br className="hidden sm:inline" />
            <span className="bg-gradient-to-r from-amber-300 via-yellow-300 to-amber-400 bg-clip-text text-transparent">Project, Delhi</span>
          </h1>

          <p className="mt-4 sm:mt-5 text-white/80 font-light tracking-wider max-w-md sm:max-w-xl animate-[fadeUp_1s_ease-out]"
            style={{ fontSize: 'clamp(0.9rem, 2vw, 1.35rem)' }}>
            Reimagining India's capital with a bold vision — where history meets tomorrow.
          </p>

          <div className="absolute bottom-8 sm:bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce">
            <span className="text-white/50 text-[10px] tracking-[0.25em] uppercase font-medium">Scroll</span>
            <FaChevronDown className="w-4 h-4 text-white/50" />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          3. ABOUT + VISION STATEMENT
      ══════════════════════════════════════════════════════════════════ */}
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-8 pt-20 sm:pt-24 pb-16 sm:pb-24">

        <SectionLabel>About the Project</SectionLabel>
        <SectionHeading>The New Lutyens Vision</SectionHeading>

        <Reveal className="max-w-3xl mx-auto mb-16 sm:mb-20">
          <p className="text-center text-gray-600 leading-[1.85] text-base sm:text-lg md:text-xl bg-white rounded-xl sm:rounded-2xl shadow-lg shadow-gray-200/60 px-5 sm:px-8 py-8 sm:py-10 border border-gray-100 relative overflow-hidden">
            <span className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-amber-500 to-yellow-400 rounded-r-full" />
            The redevelopment project represents a bold reimagining of this historic area. Without expanding its footprint, this transformation replaces aging colonial-era structures with modern, efficient buildings designed for contemporary needs. The project retains the iconic character while breathing new life into the original vision of Sir Edwin Lutyens and Herbert Baker.
          </p>
        </Reveal>

        {/* Vision Statement — Glassmorphic */}
        <Reveal direction="scale" className="mb-20 sm:mb-24">
          <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl text-white px-5 sm:px-8 py-12 sm:py-16 md:px-16 md:py-20 shadow-2xl"
            style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>
            <div className="absolute -top-32 -left-32 w-72 sm:w-96 h-72 sm:h-96 bg-amber-500/15 rounded-full blur-3xl pointer-events-none animate-[blobMove_12s_ease-in-out_infinite_alternate]" />
            <div className="absolute -bottom-32 -right-32 w-72 sm:w-96 h-72 sm:h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none animate-[blobMove_14s_ease-in-out_2s_infinite_alternate-reverse]" />
            <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle,white 1px,transparent 1px)', backgroundSize: '30px 30px' }} />

            <div className="relative z-10">
              <span className="inline-flex items-center gap-2 text-amber-400/80 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-4">
                <HiOutlineSparkles className="w-3.5 h-3.5" /> Core Philosophy
              </span>
              <h2 className="font-extrabold mb-5 sm:mb-6 text-white leading-tight" style={{ fontSize: 'clamp(1.5rem, 3.5vw, 2.8rem)' }}>
                Replacing, <span className="bg-gradient-to-r from-amber-300 to-yellow-300 bg-clip-text text-transparent">Not Expanding</span>
              </h2>
              <p className="text-gray-300/90 max-w-4xl leading-[1.85] text-sm sm:text-base md:text-lg">
                The transformation respects the original boundaries — no new land is acquired, no green spaces are sacrificed. Instead, the project strategically replaces outdated buildings with state-of-the-art structures that consolidate scattered facilities, improve efficiency, and provide modern amenities while preserving the area's heritage character. Historic landmarks and memorials remain untouched, safeguarded for eternity.
              </p>
              <div className="mt-6 sm:mt-8 flex items-center gap-3">
                <div className="h-px flex-1 bg-gradient-to-r from-amber-500/50 to-transparent" />
                <span className="text-amber-400/60 text-xs tracking-widest">✦</span>
                <div className="h-px flex-1 bg-gradient-to-l from-amber-500/50 to-transparent" />
              </div>
            </div>
          </div>
        </Reveal>

        {/* ══════════════════════════════════════════════════════════════
            4. KEY PILLARS
        ══════════════════════════════════════════════════════════════ */}
        <SectionLabel>Guiding Principles</SectionLabel>
        <SectionHeading>Key Pillars of the Project</SectionHeading>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6 mb-20 sm:mb-24">
          {keyPillars.map((p, i) => (
            <Reveal key={i} delay={i * 100} direction={i % 2 === 0 ? 'left' : 'right'}>
              <div className="group flex gap-4 sm:gap-5 items-start bg-white rounded-xl sm:rounded-2xl p-5 sm:p-7 border border-gray-100 shadow-lg shadow-gray-200/40 hover:shadow-xl hover:-translate-y-1 transition-all duration-500">
                <div className="shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 text-white flex items-center justify-center shadow-lg shadow-amber-500/25 group-hover:scale-110 transition-transform duration-500">
                  <p.Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1.5">{p.title}</h3>
                  <p className="text-gray-500 text-sm sm:text-[0.95rem] leading-relaxed">{p.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════════════════
            5. BEFORE & AFTER — Interactive Sliders
        ══════════════════════════════════════════════════════════════ */}
        <SectionLabel>Visual Comparison</SectionLabel>
        <SectionHeading>Before &amp; After: The Transformation</SectionHeading>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-20 sm:mb-24">
          {comparisons.map((c, i) => (
            <Reveal key={i} delay={i * 80} direction={i % 2 === 0 ? 'left' : 'right'}>
              <ComparisonSlider beforeSrc={c.before} afterSrc={c.after} title={c.title} />
            </Reveal>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════════════════
            6. VIDEO SHOWCASE
        ══════════════════════════════════════════════════════════════ */}
        <SectionLabel>Video Gallery</SectionLabel>
        <SectionHeading>Transformation of The Lutyen's</SectionHeading>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6 mb-20 sm:mb-24">
          {videoSlides.map((video, index) => (
            <Reveal key={index} delay={index * 120} direction="up">
              <div className="group relative overflow-hidden rounded-xl sm:rounded-2xl cursor-pointer shadow-xl shadow-black/10 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                onClick={() => handleVideoClick(index)}>
                <video className="w-full h-[240px] sm:h-[300px] md:h-[360px] object-cover transition-transform duration-700 group-hover:scale-105"
                  autoPlay muted loop playsInline poster={video.poster}>
                  {video.sources.map((s, j) => <source key={j} src={s.src} type={s.type} />)}
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/90 backdrop-blur-md flex items-center justify-center opacity-0 group-hover:opacity-100 scale-50 group-hover:scale-100 transition-all duration-500 shadow-2xl border border-white/30">
                    <FaPlay className="w-5 h-5 sm:w-6 sm:h-6 text-gray-900 ml-0.5" />
                  </div>
                </div>
                <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <span className="text-white text-xs sm:text-sm font-medium">Video {index + 1}</span>
                  <span className="text-white/60 text-[10px] sm:text-xs flex items-center gap-1"><FaExpand className="w-3 h-3" /> Expand</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Lightbox open={isOpen} close={() => setIsOpen(false)} slides={videoSlides} index={currentIndex}
          plugins={[Video, Zoom]} video={{ autoPlay: true, controls: true, muted: false }}
          zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true }} animation={{ fade: 300, swipe: 400 }}
          carousel={{ finite: false, preload: 2 }} controller={{ closeOnBackdropClick: true }}
          styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.97)' } }} />

        {/* ══════════════════════════════════════════════════════════════
            7. MAIN VIDEO — Cinematic
        ══════════════════════════════════════════════════════════════ */}
        <Reveal direction="scale" className="mb-20 sm:mb-24">
          <SectionLabel>Cinematic Experience</SectionLabel>
          <SectionHeading>Experience the Transformation</SectionHeading>

          <div className="relative max-w-5xl mx-auto group">
            <div className="absolute -inset-3 sm:-inset-4 bg-gradient-to-r from-amber-500/20 via-yellow-500/10 to-amber-500/20 rounded-2xl sm:rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl shadow-black/20 ring-1 ring-gray-200/50">
              <video className="w-full h-auto block" autoPlay muted loop playsInline controls poster="/assets/future/vision-poster.jpg">
                <source src="/assets/future/lutyens-vision.mp4" type="video/mp4" />
                <source src="/assets/future/lutyens-vision.webm" type="video/webm" />
              </video>
            </div>
          </div>
          <p className="mt-6 sm:mt-8 text-gray-400 italic text-sm sm:text-base md:text-lg max-w-2xl mx-auto text-center">
            A cinematic journey through the reimagined Lutyens Delhi — where heritage meets innovation
          </p>
        </Reveal>



        {/* ══════════════════════════════════════════════════════════════
            9. FEATURES — What's Being Built
        ══════════════════════════════════════════════════════════════ */}
        <SectionLabel>Key Highlights</SectionLabel>
        <SectionHeading>What's Being Built</SectionHeading>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 mb-20 sm:mb-24">
          {features.map((f, i) => (
            <Reveal key={i} delay={i * 100} direction="up">
              <div className="group relative bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-lg shadow-gray-200/50 hover:shadow-2xl hover:-translate-y-3 transition-all duration-500 h-full flex flex-col overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-50 to-yellow-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl sm:rounded-2xl" />
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 to-yellow-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                <div className="relative z-10 flex flex-col h-full">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 text-white flex items-center justify-center mb-4 sm:mb-5 shadow-lg shadow-amber-500/25 group-hover:scale-110 transition-transform duration-500">
                    <f.Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{f.title}</h3>
                  <p className="text-gray-500 leading-relaxed text-sm sm:text-[0.95rem] flex-1">{f.desc}</p>
                  <div className="mt-3 sm:mt-4 flex items-center gap-2 text-amber-600 font-medium text-xs sm:text-sm opacity-0 group-hover:opacity-100 transition-all duration-500">
                    Learn more <FaArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>



      </div>



      {/* ── Global Keyframes ── */}
      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:translateY(0)}}
        @keyframes float{from{transform:translateY(0) translateX(0)}to{transform:translateY(-30px) translateX(15px)}}
        @keyframes blobMove{from{transform:translate(0,0) scale(1)}to{transform:translate(40px,-30px) scale(1.15)}}
      `}</style>
    </div>
  );
}
